import React, { useCallback, useEffect, useState } from 'react';
import styles from './AiUsageTab.module.css';
import { aiUsageApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import { formatTokens } from '../../../lib/formatTokens';
import { estimatedCostUsd, formatCostUsd, formatCostUsdFromNano, effectiveInput } from '../../../lib/tokenCost';
import DateRangeFilter from '../../../ui/dateRangeFilter/DateRangeFilter';
import StatCard from '../../../ui/statCard/StatCard';
import TokenIOChart from '../../../ui/tokenIOChart/TokenIOChart';
import AdminTokenIOChart from '../../../ui/tokenIOChart/AdminTokenIOChart';
import { useAuthStore } from '../../../store/authStore';
import { UserRole } from '../../../enums/userRole';
import { UsageSummary, UsageRecord, DailyUsage } from './types';

const PAGE_SIZE = 50;

const AiUsageTab: React.FC = () => {
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const [showCacheDetail, setShowCacheDetail] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [daily, setDaily] = useState<DailyUsage[]>([]);
  const [records, setRecords] = useState<UsageRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };
      const [s, d, r] = await Promise.all([
        aiUsageApi.getSummary(params),
        aiUsageApi.getDaily(params),
        aiUsageApi.getRecords({ ...params, limit: PAGE_SIZE, offset }),
      ]);
      setSummary(s);
      setDaily(d);
      setRecords(r.rows);
      setTotal(r.total);
    } catch {
      toast.error('Failed to load AI usage');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [dateFrom, dateTo]);

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <h2>AI Usage</h2>
        <p>Token usage across all AI Builder chats and generation helpers.</p>
      </div>

      <div className={styles.toolbar}>
        <DateRangeFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={({ dateFrom: f, dateTo: t }) => { setDateFrom(f); setDateTo(t); }}
        />
      </div>

      {loading && !summary ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <>
          {summary && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Summary</h3>
              <div className={styles.cards}>
                <StatCard label="Input Tokens" value={formatTokens(summary.inputTokens)} />
                <StatCard label="Cache Read" value={formatTokens(summary.cacheReadTokens)} />
                <StatCard label="Cache Write" value={formatTokens(summary.cacheWriteTokens)} />
                <StatCard label="Output Tokens" value={formatTokens(summary.outputTokens)} />
                <StatCard
                  label={summary.totalCostUsdNano !== undefined ? 'Cost' : 'Est. Cost'}
                  value={summary.totalCostUsdNano !== undefined ? formatCostUsdFromNano(summary.totalCostUsdNano) : formatCostUsd(estimatedCostUsd(summary))}
                />
                <StatCard label="Runs" value={String(summary.runCount)} />
                <StatCard label="Debug Runs" value={String(summary.debugRunCount)} tone={summary.debugRunCount > 0 ? 'red' : 'default'} />
                <StatCard label="Users" value={String(summary.userCount)} />
              </div>
            </section>
          )}

          {daily.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Tokens over time</h3>
                {isSuperAdmin && (
                  <label className={styles.cacheToggle}>
                    <input
                      type="checkbox"
                      checked={showCacheDetail}
                      onChange={e => setShowCacheDetail(e.target.checked)}
                    />
                    Show cache breakdown
                  </label>
                )}
              </div>
              <div className={styles.chart}>
                {isSuperAdmin && showCacheDetail
                  ? <AdminTokenIOChart data={daily} />
                  : <TokenIOChart data={daily} />
                }
              </div>
            </section>
          )}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Activity</h3>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th><th>User</th><th>Tool</th><th>Mode</th><th>Debug</th><th>Model</th>
                  <th>Eff. Input</th><th>Output</th><th>Cost</th><th>Calls</th><th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.userEmail ?? r.userId ?? '—'}</td>
                    <td>{r.toolTitle ?? r.generationType ?? r.kind}</td>
                    <td>{r.toolMode ?? '—'}</td>
                    <td>{r.debugMode ? <span className={styles.debugBadge}>debug</span> : '—'}</td>
                    <td>{r.model ?? '—'}</td>
                    <td>{formatTokens(effectiveInput(r))}</td>
                    <td>{formatTokens(r.outputTokens)}</td>
                    <td>{r.totalCostUsdNano !== undefined ? formatCostUsdFromNano(r.totalCostUsdNano) : formatCostUsd(estimatedCostUsd(r, r.model))}</td>
                    <td>{r.llmCallCount}</td>
                    <td>{r.outcome ?? '—'}</td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr><td colSpan={11} className={styles.empty}>No usage in this range.</td></tr>
                )}
                </tbody>
              </table>
            </div>

            <div className={styles.pager}>
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
              <span>{total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
              <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AiUsageTab;
